import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import PageHeader from '../../components/PageHeader/PageHeader';
import Button from '../../components/Button/Button';
import styles from './Projects.module.css';

import oms1 from '../../assets/oms/oms1.png';
import oms2 from '../../assets/oms/oms2.png';
import oms5 from '../../assets/oms/oms5.png';
import oms8 from '../../assets/oms/oms8.png';
import oms10 from '../../assets/oms/oms10.png';
import oms11 from '../../assets/oms/oms11.png';
import oms12 from '../../assets/oms/oms12.png';
import oms13 from '../../assets/oms/oms13.png';
import oms15 from '../../assets/oms/oms15.png';
import oms16 from '../../assets/oms/oms16.png';
import oms17 from '../../assets/oms/oms17.png';

const agroOmsIcons = [oms1, oms2, oms5, oms8, oms10, oms11, oms12, oms13, oms15, oms16, oms17];

// Placeholder Constants for AGRO - SACHA'A WARMI
const agroCommunityImage = "https://assets.cinbiosis.com/projects/sachaa-warmi/header.jpg";
const agroCarouselImages = [
  "https://assets.cinbiosis.com/projects/sachaa-warmi/mapa1.jpeg",
  "https://assets.cinbiosis.com/projects/sachaa-warmi/mapa2.jpeg",
  "https://assets.cinbiosis.com/projects/sachaa-warmi/mapa3.jpeg"
];
const agroDownloadFile = "https://assets.cinbiosis.com/projects/sachaa-warmi/Agro-Sacha'a%20Warmi-2026-es.pdf";
const agroDownloadFileEn = "https://assets.cinbiosis.com/projects/sachaa-warmi/Agro-Sacha'a%20Warmi-2026-en.pdf";

const Projects = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isEn = lang === 'en';
  const contactPath = `/${lang}/${isEn ? 'contact' : 'contacto'}`;

  const [openProjectId, setOpenProjectId] = useState(null);

  const toggleProject = (id) => {
    setOpenProjectId(openProjectId === id ? null : id);
  };

  const projectsData = [
    {
      id: 'agro',
      type: 'agro',
      status: t('projectsPage.inProgress'),
      statusClass: styles.inProgress,
      title: t('projectsPage.projects.agro.title'),
      category: t('projectsPage.projects.agro.category')
    },
    {
      id: 'p2',
      type: 'standard',
      status: t('projectsPage.inProgress'),
      statusClass: styles.inProgress,
      title: t('projectsPage.projects.p2.title'),
      category: t('projectsPage.projects.p2.category'),
      desc: t('projectsPage.projects.p2.desc'),
      scope: [
        t('projectsPage.projects.p2.s1'),
        t('projectsPage.projects.p2.s2'),
        t('projectsPage.projects.p2.s3'),
        t('projectsPage.projects.p2.s4')
      ],
      results: [
        { n: t('projectsPage.projects.p2.r1N'), l: t('projectsPage.projects.p2.r1L') },
        { n: t('projectsPage.projects.p2.r2N'), l: t('projectsPage.projects.p2.r2L') },
        { n: t('projectsPage.projects.p2.r3N'), l: t('projectsPage.projects.p2.r3L') }
      ]
    },
    {
      id: 'p1',
      type: 'standard',
      status: t('projectsPage.executed'),
      title: t('projectsPage.projects.p1.title'),
      category: t('projectsPage.projects.p1.category'),
      desc: t('projectsPage.projects.p1.desc'),
      scope: [
        t('projectsPage.projects.p1.s1'),
        t('projectsPage.projects.p1.s2'),
        t('projectsPage.projects.p1.s3'),
        t('projectsPage.projects.p1.s4')
      ],
      results: [
        { n: t('projectsPage.projects.p1.r1N'), l: t('projectsPage.projects.p1.r1L') },
        { n: t('projectsPage.projects.p1.r2N'), l: t('projectsPage.projects.p1.r2L') },
        { n: t('projectsPage.projects.p1.r3N'), l: t('projectsPage.projects.p1.r3L') },
        { n: t('projectsPage.projects.p1.r4N'), l: t('projectsPage.projects.p1.r4L') },
        { n: t('projectsPage.projects.p1.r5N'), l: t('projectsPage.projects.p1.r5L') }
      ]
    }
  ];

  const renderStandardContent = (project) => (
    <div className={styles.projectBody}>
      <div className={styles.projectDescription}>
        <p>{project.desc}</p>
        <h4>{t('projectsPage.scopeTitle')}</h4>
        <ul className={styles.scopeList}>
          {project.scope.map((item, idx) => (
            <li key={idx}><CheckCircle size={16} /> {item}</li>
          ))}
        </ul>
      </div>
      <div className={styles.projectResults}>
        <h4>{project.id === 'p1' ? t('projectsPage.resultsTitle') : t('projectsPage.progressTitle')}</h4>
        <div className={styles.resultsGrid}>
          {project.results.map((res, idx) => (
            <div key={idx} className={styles.resultItem}>
              <span className={styles.resultNumber}>{res.n}</span>
              <span className={styles.resultLabel}>{res.l}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAgroContent = () => (
    <div className={styles.agroProjectCard}>
      <h3 className={styles.agroProjectTitle}>{t('projectsPage.projects.agro.cardTitle')}</h3>

      <div className={styles.agroHeroImageContainer}>
        <img src={agroCommunityImage} alt="Comunidad Sacha'a Warmi" className={styles.agroHeroImage} />
      </div>

      <div className={styles.agroContentGrid}>
        <div className={styles.agroLeftColumn}>
          <div className={styles.agroCarousel}>
            {agroCarouselImages.map((src, idx) => (
              <img key={idx} src={src} alt={`Carrusel ${idx + 1}`} className={styles.agroCarouselImage} />
            ))}
          </div>
          <div className={styles.agroTextBox}>
            <p>{t('projectsPage.projects.agro.desc')}</p>
          </div>
        </div>

        <div className={styles.agroRightColumn}>
          <div className={styles.agroInfoCard}>
            <div className={styles.agroInfoItem}>
              <strong>{t('projectsPage.projects.agro.whereWhenTitle')}</strong>
              <p>{t('projectsPage.projects.agro.whereWhenValue')}</p>
            </div>
            <div className={styles.agroInfoItem}>
              <strong>{t('projectsPage.projects.agro.tecTitle')}</strong>
              <p>{t('projectsPage.projects.agro.tecValue')}</p>
            </div>
            <div className={styles.agroOmsContainer}>
              {agroOmsIcons.map((icon, idx) => (
                <img key={idx} src={icon} alt={`Objetivo OMS ${idx + 1}`} className={styles.agroOmsImage} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.agroDownloadRow}>
        <a href={isEn ? agroDownloadFileEn : agroDownloadFile} target="_blank" rel="noopener noreferrer" className={styles.downloadButton}>
          {t('projectsPage.projects.agro.downloadBtn')}
        </a>
      </div>
    </div>
  );

  return (
    <div className={styles.projectsPage}>
      <SEO
        title={t('seo.projectsTitle')}
        description={t('seo.projectsDesc')}
        canonicalPath={`/${isEn ? 'projects' : 'proyectos'}`}
      />

      <PageHeader
        title={t('projectsPage.headerTitle')}
        description={t('projectsPage.headerDesc')}
      />

      <section className={styles.projectsSection}>
        <div className="container">
          <div className={styles.projectsAccordion}>
            {projectsData.map((project) => {
              const isOpen = openProjectId === project.id;

              return (
                <div key={project.id} className={`${styles.accordionItem} ${isOpen ? styles.open : ''}`}>
                  <button
                    className={styles.accordionButton}
                    onClick={() => toggleProject(project.id)}
                    aria-expanded={isOpen}
                  >
                    <div className={styles.accordionHeaderText}>
                      <div className={styles.accordionMeta}>
                        <span className={`${styles.statusBadge} ${project.statusClass || ''}`}>{project.status}</span>
                        {project.category && <span className={styles.accordionCategory}>{project.category}</span>}
                      </div>
                      <h2 className={styles.accordionTitle}>{project.title}</h2>
                    </div>
                    <div className={styles.accordionIcon}>
                      {isOpen ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className={styles.accordionPanel}>
                      {project.type === 'standard' ? renderStandardContent(project) : renderAgroContent()}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className="container text-center">
          <h2>{t('projectsPage.ctaTitle')}</h2>
          <p>{t('projectsPage.ctaSubtitle')}</p>
          <Button to={contactPath} variant="primary">{t('projectsPage.ctaBtn')}</Button>
        </div>
      </section>
    </div>
  );
};

export default Projects;
